import {
  HeadItemType,
  Icon,
  Modal,
  Page,
  Search,
  SlimButton,
  Sidebar,
  Table,
  Breadcrumb
} from 'components'
import { useEffect, useRef, useState } from 'react'
import * as S from './styles'

import { Controller, FormProvider, useForm } from 'react-hook-form'
import { getCustomers } from 'services/shared/customer'

import { toast } from 'react-toastify'

const ICON_SIZE = '18'
const DEFAULT_PAGE = 1
const MAX_LIST_SIZE = 50

import { URL_CONST } from 'constants/urls'
import { addKeyAccount, deleteKeyAccount, getKeyAccounts } from 'services/keyAccounts'
import { TrackingModal } from './components/TrackingModal'
import { theme } from 'styles/theme/hav-theme'
import type { KeyAccount } from 'services/keyAccounts/types'

// NOTE: This type is used to map Customer the old customer data to be used by the search component
// TODO: Remove this type when the search component is updated to use the new customer data
type CustomerSearch = {
  cd_clie: string
  nm_clie: string
  nr_cnpj_cpf: string
}

const headDataCustomers: HeadItemType[] = [
  { label: 'Razão social', columnName: 'name' },
  { label: 'Código SAP', columnName: 'id' },
  { label: 'CNPJ', columnName: 'cnpj' },
  { label: '', columnName: '', actionsLabel: true }
]

const KeyAccounts = () => {
  const searchClientRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [openTracking, setOpenTracking] = useState(false)
  const [selectedKeyAccount, setSelectedKeyAccount] = useState<KeyAccount | null>(null)
  const [keyAccounts, setKeyAccounts] = useState<KeyAccount[]>([])

  const defaultFields: {
    customer: string
  } = {
    customer: ''
  }

  const methods = useForm({
    defaultValues: defaultFields
  })

  const { control } = methods

  const fetchKeyAccounts = async () => {
    setLoading(true)

    try {
      const { data: keyAccounts } = await getKeyAccounts()

      setKeyAccounts(keyAccounts)
    } catch (error) {
      toast.error('Erro ao buscar clientes!')
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomers = async (
    inputValue: string,
    page: string
  ): Promise<CustomerSearch[]> => {
    try {
      const { data: customers } = await getCustomers(
        {
          search: inputValue.toLocaleUpperCase()
        },
        {
          page: DEFAULT_PAGE,
          perPage: MAX_LIST_SIZE
        }
      )

      return customers.map((customer) => {
        return {
          cd_clie: customer.id,
          nm_clie: customer.name,
          nr_cnpj_cpf: customer.cnpjFormatted!
        }
      })
    } catch (error) {
      toast.error('Erro ao buscar clientes!')
      return []
    }
  }

  const checkKeyAccountExists = () => {
    if (!selectedKeyAccount) return

    const keyAccount = keyAccounts.find(
      (keyAccount) => keyAccount.id === selectedKeyAccount.id
    )

    return keyAccount
  }

  const handleAddKeyAccount = async () => {
    try {
      setShowAddModal(false)
      const keyAccount = checkKeyAccountExists()

      if (keyAccount) {
        toast.warning(
          'Não é possível adicionar o cliente, pois ele já pertence à categoria Key Accounts'
        )

        return
      }

      if (!selectedKeyAccount) return

      setLoading(true)
      await addKeyAccount(selectedKeyAccount.id)

      fetchKeyAccounts()
      toast.success('Cliente adicionado com sucesso!')
    } catch (error) {
      setLoading(false)
      toast.error('Erro ao adicionar o cliente!')
      console.error('Error adding key account:', error)
    } finally {
      setSelectedKeyAccount(null)
    }
  }

  const handleDeleteKeyAccount = async () => {
    try {
      setShowDeleteModal(false)
      const keyAccount = checkKeyAccountExists()

      if (keyAccount) {
        setLoading(true)
        if (!selectedKeyAccount) return

        await deleteKeyAccount(selectedKeyAccount.id)

        setKeyAccounts(
          keyAccounts.filter((keyAccount) => keyAccount.id !== selectedKeyAccount.id)
        )

        toast.success('Cliente excluído com sucesso!')
        return
      }
    } catch (error) {
      toast.error('Erro ao excluir o cliente!')
      console.error('Error deleting key account:', error)
    } finally {
      setLoading(false)
      setSelectedKeyAccount(null)
    }
  }

  const handleOpenTracking = () => {
    setOpenTracking(true)
  }

  useEffect(() => {
    fetchKeyAccounts()
  }, [])

  const renderBody = () => {
    return keyAccounts.map((keyAccount) => {
      const key = `${keyAccount.id}`

      return {
        id: key,
        content: [
          <span key={`name-${key}`}>{keyAccount.name}</span>,
          <span key={`id-${key}`}>{keyAccount.id}</span>,
          <span key={`cnpj-${key}`}>{keyAccount.cnpjFormatted}</span>,
          <span key={`actions-${key}`}>
            {
              <SlimButton
                variant="icon"
                onClick={() => {
                  setShowDeleteModal(true)
                  setSelectedKeyAccount(keyAccount)
                }}
                icon={<Icon icon="trash" size={'20'} />}
                iconColor={theme.colors.alert.danger[50]}
              />
            }
          </span>
        ]
      }
    })
  }

  const renderTopBar = () => (
    <S.TopBar>
      <SlimButton
        data-testid="tracking-button"
        variant="ghost"
        content="Tracking"
        icon={<Icon icon="history" size={ICON_SIZE} />}
        onClick={handleOpenTracking}
        disabled={loading}
      />
    </S.TopBar>
  )

  return (
    <>
      <Page fullwidth renderHeaderComponent={renderTopBar()} hasHeader loading={loading}>
        <Breadcrumb />
        <S.Wrapper>
          {/* TODO: Add disabled or hidden when user doesn't have permission */}
          <Sidebar isCollapsed>
            <Sidebar.Divider />
            <Sidebar.Item
              label="Início"
              icon={<Icon icon="home-rounded" />}
              route={URL_CONST.ROOT}
            />
            <Sidebar.Item label="Key Accounts" icon={<Icon icon="star" />} active />
            <Sidebar.Item
              label="Lead Times"
              icon={<Icon icon="calendar-clock" />}
              route={`${URL_CONST.BACKOFFICE}${URL_CONST.LEAD_TIMES}`}
            />
          </Sidebar>
          <S.Container>
            <S.Title>Key Accounts</S.Title>
            <S.Divider />

            <FormProvider {...methods}>
              <S.Form>
                <S.FieldsContainer>
                  <S.FieldWrapper
                    templateColumns="1fr 1fr 1fr"
                    mediaTemplateColumns="1fr 1fr"
                    ref={searchClientRef}
                  >
                    <S.FieldItemWrapper>
                      <Controller
                        name="customer"
                        control={control}
                        render={({ field }) => (
                          <Search
                            key={field.name}
                            label={'Adicionar cliente'}
                            id={field.name}
                            placeHolder={'Busque por código ou nome'}
                            loadOptions={fetchCustomers}
                            type="select"
                            isClientSearch
                            optionsMaxHeight="400px"
                            optionsLimit={MAX_LIST_SIZE}
                            page={DEFAULT_PAGE}
                            keysColumnsOption={['nm_clie']}
                            loadOnFocus
                            setOptionSelected={(customer) => {
                              setSelectedKeyAccount({
                                id: customer.cd_clie,
                                name: customer.nm_clie,
                                cnpj: customer.nr_cnpj_cpf
                              })
                              setShowAddModal(true)
                            }}
                          />
                        )}
                      />
                    </S.FieldItemWrapper>
                  </S.FieldWrapper>
                </S.FieldsContainer>
              </S.Form>
            </FormProvider>

            <S.TableContainer>
              <S.TextInfo>{keyAccounts?.length} clientes totais</S.TextInfo>
              <Table
                headData={headDataCustomers}
                bodyData={renderBody()}
                rowHeight={40}
              />
            </S.TableContainer>
          </S.Container>
        </S.Wrapper>
      </Page>

      <Modal
        open={showAddModal}
        closeAction={() => setShowAddModal(false)}
        title="Adicionar cliente"
        setOpen={setShowAddModal}
        buttonAction={handleAddKeyAccount}
        buttonText="Adicionar"
        returnAction={() => setShowAddModal(false)}
        modalWidth={456}
        showReturn
        loading={loading}
      >
        <span>
          Tem certeza de que deseja adicionar o cliente <b>{selectedKeyAccount?.name}</b>{' '}
          à categoria de Key Accounts?
        </span>
      </Modal>

      <Modal
        open={showDeleteModal}
        closeAction={() => setShowDeleteModal(false)}
        title="Excluir Cliente"
        setOpen={setShowDeleteModal}
        buttonAction={handleDeleteKeyAccount}
        buttonText="Excluir"
        buttonVariant="danger"
        returnAction={() => {
          setSelectedKeyAccount(null)
          setShowDeleteModal(false)
        }}
        modalWidth={456}
        showReturn
        loading={loading}
      >
        <span>
          Tem certeza de que deseja excluir o cliente <b>{selectedKeyAccount?.name}</b> à
          categoria de Key Accounts?
        </span>
      </Modal>

      {openTracking && <TrackingModal setOpen={setOpenTracking} />}
    </>
  )
}

export default KeyAccounts
